import { useMemo, useState } from "react";
import { MessageSquarePlus, Search, SquarePen } from "lucide-react";
import { PageHeader } from "@/components/common/page-header";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { useAuthStore } from "@/features/auth/store/auth-store";
import { cn } from "@/lib/utils";

type ChatFilter = "all" | "unread" | "group";
const CHAT_FILTERS: { value: ChatFilter; label: (unread: number) => string }[] = [
  { value: "all", label: () => "All" },
  { value: "unread", label: (n) => (n > 0 ? `Unread · ${n}` : "Unread") },
  { value: "group", label: () => "Groups" },
];
import { useChatStore } from "@/features/chat/store/chat-store";
import { useChatSocket } from "@/features/chat/hooks/use-chat-socket";
import { conversationTitle } from "@/features/chat/utils";
import { ConversationList } from "@/features/chat/components/conversation-list";
import { ConversationContextPanel } from "@/features/chat/components/conversation-context-panel";
import { MessageThread } from "@/features/chat/components/message-thread";
import { NewConversationDialog } from "@/features/chat/components/new-conversation-dialog";

export default function ChatPage() {
  useChatSocket();
  const conversations = useChatStore((s) => s.conversations);
  const activeId = useChatStore((s) => s.activeId);
  const loading = useChatStore((s) => s.loadingConversations);
  const openConversation = useChatStore((s) => s.openConversation);
  const onlineIds = useChatStore((s) => s.onlineIds);
  const meId = useAuthStore((s) => s.user?.id ?? null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [filter, setFilter] = useState<ChatFilter>("all");
  const [query, setQuery] = useState("");

  const active = conversations.find((c) => c.id === activeId) ?? null;

  const unreadConvCount = conversations.filter((c) => c.unreadCount > 0).length;
  const totalUnread = conversations.reduce((n, c) => n + c.unreadCount, 0);
  const onlineTeammates = onlineIds.filter((id) => id !== meId).length;

  const visibleConversations = useMemo(() => {
    const byFilter =
      filter === "all"
        ? conversations
        : filter === "group"
          ? conversations.filter((c) => c.kind === "group")
          : conversations.filter((c) => c.unreadCount > 0);
    const q = query.trim().toLowerCase();
    if (!q) return byFilter;
    return byFilter.filter((c) => conversationTitle(c, meId).toLowerCase().includes(q));
  }, [conversations, filter, query, meId]);

  return (
    <>
      <PageHeader
        title="Team chat"
        subtitle={
          <>
            <strong className="text-foreground font-semibold">
              {totalUnread} unread
            </strong>{" "}
            across {unreadConvCount} {unreadConvCount === 1 ? "conversation" : "conversations"} ·{" "}
            {onlineTeammates} {onlineTeammates === 1 ? "teammate" : "teammates"} online
          </>
        }
      />

      <Card className="flex h-[calc(100vh-15rem)] w-full flex-row gap-0 overflow-hidden p-0">
        {/* Conversation list */}
        <div className="flex w-full max-w-[340px] shrink-0 flex-col border-r sm:w-[340px]">
          <div className="flex shrink-0 flex-col gap-2.5 px-4 pt-4 pb-2.5">
            <div className="flex items-center justify-between">
              <h2 className="text-lg font-semibold tracking-[-0.01em]">Messages</h2>
              <Button
                type="button"
                size="icon"
                variant="outline"
                aria-label="New conversation"
                className="text-info size-9"
                onClick={() => setDialogOpen(true)}
              >
                <SquarePen className="size-[18px]" />
              </Button>
            </div>
            {/* Search over already-loaded conversations (client-side, by title). */}
            <div className="bg-muted flex h-9.5 items-center gap-2 rounded-md px-3">
              <Search className="text-muted-foreground size-[15px] shrink-0" />
              <input
                type="search"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                aria-label="Search conversations"
                placeholder="Search people or groups…"
                className="text-foreground min-w-0 flex-1 border-0 bg-transparent text-[13px] outline-none"
              />
            </div>
            {/* Filter the thread list (QA: filters on top of the chat). */}
            <div className="bg-muted flex gap-0.5 rounded-sm p-[3px]" role="group" aria-label="Filter">
              {CHAT_FILTERS.map((f) => (
                <button
                  key={f.value}
                  type="button"
                  aria-pressed={filter === f.value}
                  onClick={() => setFilter(f.value)}
                  className={cn(
                    "h-7.5 flex-1 rounded-[10px] text-xs font-medium transition-colors",
                    filter === f.value
                      ? "bg-card text-foreground shadow-card font-semibold"
                      : "text-muted-foreground hover:text-foreground",
                  )}
                >
                  {f.label(unreadConvCount)}
                </button>
              ))}
            </div>
          </div>
          <div className="min-h-0 flex-1 overflow-y-auto px-2 pb-3">
            {loading && conversations.length === 0 ? (
              <p className="text-muted-foreground px-3 py-8 text-center text-sm">Loading…</p>
            ) : (
              <ConversationList
                conversations={visibleConversations}
                activeId={activeId}
                showSections={filter === "all" && !query.trim()}
                onSelect={(id) => void openConversation(id)}
              />
            )}
          </div>
        </div>

        {/* Thread pane */}
        <div className="min-w-0 flex-1">
          {active ? (
            <MessageThread conversation={active} />
          ) : (
            <div className="flex h-full flex-col items-center justify-center gap-3 text-center">
              <MessageSquarePlus className="text-muted-foreground size-10" />
              <p className="text-muted-foreground text-sm">
                Select a conversation, or start a new one.
              </p>
              <Button type="button" variant="outline" onClick={() => setDialogOpen(true)}>
                New conversation
              </Button>
            </div>
          )}
        </div>

        {/* Context panel — real content only (person, team-online, retention). */}
        {active && (
          <div className="hidden w-[272px] shrink-0 border-l xl:block">
            <ConversationContextPanel conversation={active} />
          </div>
        )}
      </Card>

      <NewConversationDialog
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        onCreated={(id) => void openConversation(id)}
      />
    </>
  );
}
